import { IReoEncryptKey } from '@reolink-cloud/interface';
import Base64 from 'crypto-js/enc-base64';
import Utf8 from 'crypto-js/enc-utf8';
import MD5 from 'crypto-js/md5';
import SHA256 from 'crypto-js/sha256';
import HmacSHA256 from 'crypto-js/hmac-sha256';
import { IAccessKeyInfo } from './stores';
import { IEncryptionMeta } from './metaResolver';
import { IKeyProvider } from './keyProvider';

const toBase64Url = ( base64: string ): string =>
    base64.replace( /\+/g, '-' ).replace( /\//g, '_' ).replace( /=+$/g, '' );

export const buildAccessKeyHeader = (
    accessKeyInfo: IAccessKeyInfo
): Record<string, string> => {

    const signature = HmacSHA256( Utf8.parse( accessKeyInfo.id ), Utf8.parse( accessKeyInfo.ck ));
    const accessKey = toBase64Url( Base64.stringify( signature ));

    return {
        'X-Access-Key': accessKey
    };

};

export const buildCmkHeaders = async (
    encrypt: IReoEncryptKey,
    keySeed: string,
    keyProvider: IKeyProvider
): Promise<Record<string, string>> => {

    const keyBase64 = await keyProvider.getKeyBase64({
        kdf: encrypt.kdf as Parameters<IKeyProvider['getKeyBase64']>[0]['kdf'],
        iterations: encrypt.iterations,
        devicePublicKey: encrypt.devicePublicKey,
        keySeed,
        cmkId: encrypt.cmkId
    });

    const keyMD5Base64 = Base64.stringify( MD5( Base64.parse( keyBase64 )));
    const keySha256Base64 = Base64.stringify( SHA256( Base64.parse( keyBase64 )));

    if ( encrypt.mode.includes( 's3' )) {

        return {
            'x-amz-server-side-encryption-customer-key': keyBase64,
            'x-amz-server-side-encryption-customer-key-MD5': keyMD5Base64,
            'x-amz-server-side-encryption-customer-algorithm': encrypt.algo
        };

    } else if ( encrypt.mode === 'azure-sse-c' ) {

        return {
            'x-ms-encryption-algorithm': encrypt.algo,
            'x-ms-encryption-key': keyBase64,
            'x-ms-encryption-key-sha256': keySha256Base64
        };

    }

    return {};

};

/**
 * 根据加密元信息组装本次请求需要附加的全部头部。
 *
 * 与原 `HeaderPlugin` 行为保持一致：
 * - `sseC`：附加 CMK 头（`x-amz-*` / `x-ms-*`）
 * - `urlOnly`：附加 `X-Access-Key`
 * - `native` / `plain`：不附加（URL 已签名 / 无需鉴权）
 */
export const buildAuthHeaders = async (
    meta: IEncryptionMeta,
    keyProvider: IKeyProvider
): Promise<Record<string, string>> => {

    if ( meta.mode === 'sseC' && meta.encryptInfo && meta.encryptDetail ) {

        return buildCmkHeaders(
            meta.encryptDetail,
            meta.encryptInfo.keySeed,
            keyProvider
        );

    }

    if ( meta.mode === 'urlOnly' && meta.accessKeyInfo ) {

        return buildAccessKeyHeader( meta.accessKeyInfo );

    }

    return {};

};
